import { persistTripleValue, readStoredTriple } from './triple-storage';

/** 无后端 user_id 或未登录时的桶 key（与业务数字串 userId 区分） */
const GUEST_BUCKET_KEY = '_guest';

/** 分桶键来源：静态字符串，或调用时再解析（便于组合 userId / deviceId / 登录态等）。 */
export type TFrequencyBucketKeySource = string | null | undefined | (() => string | null | undefined);

export interface IFrequencyControlOptions {
    /** 在当前浏览器存储维度下的累计上限，默认 3 */
    maxLifetime?: number;
    /** 每个自然日（本地时区）最多执行次数，默认 1 */
    maxPerDay?: number;
    /**
     * 与上次弹出日期的自然日间隔（本地），需 **≥** 该值才允许再次弹出。
     * 默认 0：不启用；传 2 表示昨天弹过则今天不会再弹等业务上的「至少隔开一个自然日」。
     */
    minCalendarDaysBetweenPrompts?: number;
    /**
     * 频控分桶键；同一键下共用计数序列，不同键各自保留在同一 JSON 对象里。
     * 未传、空串或函数返回空 → `_guest`。旧版扁平结构会迁移到 `_guest`。
     */
    bucketKey?: TFrequencyBucketKeySource;
}

interface IStoredState {
    totalCount: number;
    lastYmd: string;
    dayCount: number;
}

function localYmd(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function nonNegativeInt(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return Math.max(0, Math.floor(value));
    }
    return undefined;
}

function normalizeBucketKey(raw?: string | null): string {
    const t = typeof raw === 'string' ? raw.trim() : '';
    return t.length > 0 ? t : GUEST_BUCKET_KEY;
}

function resolveBucketKey(source?: TFrequencyBucketKeySource): string {
    if (typeof source === 'function') {
        return normalizeBucketKey(source());
    }
    return normalizeBucketKey(source);
}

function parseStateObject(obj: unknown): IStoredState {
    if (!obj || typeof obj !== 'object') {
        return { totalCount: 0, lastYmd: '', dayCount: 0 };
    }
    const o = obj as Partial<IStoredState & { count?: number }>;
    const totalCount = nonNegativeInt(o.totalCount) ?? nonNegativeInt(o.count) ?? 0;
    const lastYmd = typeof o.lastYmd === 'string' ? o.lastYmd : '';
    const dayCount = nonNegativeInt(o.dayCount) ?? 0;
    return { totalCount, lastYmd, dayCount };
}

function isLegacyFlatRoot(obj: Record<string, unknown>): boolean {
    if (typeof obj.totalCount === 'number' || typeof obj.count === 'number') {
        return true;
    }
    const legacyKeys = new Set(['totalCount', 'lastYmd', 'dayCount', 'count']);
    const keys = Object.keys(obj);
    if (keys.length === 0 || keys.length > legacyKeys.size) {
        return false;
    }
    return keys.every(k => legacyKeys.has(k));
}

function isPerUserMap(obj: Record<string, unknown>): boolean {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
        return false;
    }
    return keys.every(k => {
        const v = obj[k];
        if (!v || typeof v !== 'object' || Array.isArray(v)) {
            return false;
        }
        return 'totalCount' in v || 'dayCount' in v || 'lastYmd' in v;
    });
}

/** 整条存储：{ [userId 或 _guest]: IStoredState }；兼容历史上扁平的一层结构。 */
function parseRoot(raw: string | null): Record<string, IStoredState> {
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return {};
        }
        const obj = parsed as Record<string, unknown>;

        if (isLegacyFlatRoot(obj)) {
            return { [GUEST_BUCKET_KEY]: parseStateObject(obj) };
        }

        if (isPerUserMap(obj)) {
            const out: Record<string, IStoredState> = {};
            Object.keys(obj).forEach(k => {
                out[k] = parseStateObject(obj[k]);
            });
            return out;
        }

        return {};
    } catch {
        return {};
    }
}

function storageKey(featureKey: string): string {
    return `fc_${featureKey}`;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** 同一天为 0，次日为 1；解析失败返回 null（不拦截）。 */
function calendarDaysBetweenLocal(fromYmd: string, toYmd: string): number | null {
    const split = (s: string): [number, number, number] | null => {
        const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
        if (!p) {
            return null;
        }
        return [Number(p[1]), Number(p[2]), Number(p[3])];
    };
    const a = split(fromYmd);
    const b = split(toYmd);
    if (!a || !b) {
        return null;
    }
    const da = new Date(a[0], a[1] - 1, a[2]);
    const db = new Date(b[0], b[1] - 1, b[2]);
    if (
        Number.isNaN(da.getTime()) ||
        Number.isNaN(db.getTime()) ||
        da.getFullYear() !== a[0] ||
        da.getMonth() !== a[1] - 1 ||
        da.getDate() !== a[2] ||
        db.getFullYear() !== b[0] ||
        db.getMonth() !== b[1] - 1 ||
        db.getDate() !== b[2]
    ) {
        return null;
    }
    const diffMs = db.getTime() - da.getTime();
    return Math.round(diffMs / MS_PER_DAY);
}

/**
 * 基于三级存储的频控：默认「终身最多 3 次 + 每日最多 1 次」；自然日最小间隔默认为 0（不启用），需要时隔日再弹可传 `minCalendarDaysBetweenPrompts`。
 * 按 `bucketKey` 分桶持久化，JSON 形态为 `{ [bucketKey]: { totalCount, lastYmd, dayCount } }`；未指定时使用 `_guest` 桶。
 * 仅在允许执行时调用 callback；计数在 callback 成功返回后写入（callback 抛错则不扣次数）。
 */
export async function frequencyControl(
    key: string,
    callback: () => void,
    options?: IFrequencyControlOptions
): Promise<void> {
    const maxLifetime = options?.maxLifetime ?? 3;
    const maxPerDay = options?.maxPerDay ?? 1;
    const minCalendarDaysBetweenPrompts = options?.minCalendarDaysBetweenPrompts ?? 0;
    const bucketKey = resolveBucketKey(options?.bucketKey);

    const raw = await readStoredTriple(storageKey(key));
    const map = parseRoot(raw);
    const state = map[bucketKey] ?? { totalCount: 0, lastYmd: '', dayCount: 0 };
    const ymd = localYmd();

    const effectiveDayCount = state.lastYmd === ymd ? state.dayCount : 0;

    if (state.totalCount >= maxLifetime) {
        return;
    }

    if (minCalendarDaysBetweenPrompts > 0 && state.lastYmd) {
        const daysSinceLast = calendarDaysBetweenLocal(state.lastYmd, ymd);
        if (daysSinceLast !== null && daysSinceLast < minCalendarDaysBetweenPrompts) {
            return;
        }
    }

    if (effectiveDayCount >= maxPerDay) {
        return;
    }

    callback();

    const next: IStoredState = {
        totalCount: state.totalCount + 1,
        lastYmd: ymd,
        dayCount: state.lastYmd === ymd ? state.dayCount + 1 : 1,
    };
    map[bucketKey] = next;
    persistTripleValue(storageKey(key), JSON.stringify(map));
}
